import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { publicJob, publicJobApply } from "@/lib/companyApi";
import { useAuth } from "@/contexts/AuthContext";
import { MapPin, Briefcase, DollarSign, Calendar, Loader2, CheckCircle, AlertCircle, ArrowRight, ExternalLink } from "lucide-react";

/** Inject / remove Google for Jobs JSON-LD structured data */
function useGoogleJobsSchema(job) {
  useEffect(() => {
    if (!job) return;
    // Shared job links deserve a real title in the tab, search results and chats.
    document.title = `${job.title}${job.company_name ? ` at ${job.company_name}` : ""} · Wishire Jobs`;
    const scriptId = "wishire-gjobs-schema";
    document.getElementById(scriptId)?.remove();
    const script = document.createElement("script");
    script.id = scriptId;
    script.type = "application/ld+json";
    const schema = {
      "@context": "https://schema.org/",
      "@type": "JobPosting",
      title: job.title,
      description: job.description,
      datePosted: job.created_at ? job.created_at.slice(0, 10) : new Date().toISOString().slice(0, 10),
      hiringOrganization: {
        "@type": "Organization",
        name: job.company_name || "Company",
        sameAs: job.company_website || undefined,
      },
      jobLocation: job.remote
        ? { "@type": "Place", address: { "@type": "PostalAddress", addressCountry: "IN" } }
        : {
            "@type": "Place",
            address: {
              "@type": "PostalAddress",
              addressLocality: job.location || "",
              addressCountry: "IN",
            },
          },
      applicantLocationRequirements: job.remote
        ? { "@type": "Country", name: "India" }
        : undefined,
      jobLocationType: job.remote ? "TELECOMMUTE" : undefined,
      employmentType: {
        full_time: "FULL_TIME",
        part_time: "PART_TIME",
        contract: "CONTRACTOR",
        internship: "INTERN",
      }[job.employment_type] || "FULL_TIME",
      baseSalary:
        job.comp_min && job.comp_max
          ? {
              "@type": "MonetaryAmount",
              currency: job.currency || "INR",
              value: {
                "@type": "QuantitativeValue",
                minValue: job.comp_min,
                maxValue: job.comp_max,
                unitText: "YEAR",
              },
            }
          : undefined,
      skills: (job.skills || []).join(", ") || undefined,
      directApply: true,
    };
    // Remove undefined keys
    script.textContent = JSON.stringify(
      JSON.parse(JSON.stringify(schema, (_, v) => (v === undefined ? undefined : v)))
    );
    document.head.appendChild(script);
    return () => document.getElementById(scriptId)?.remove();
  }, [job]);
}

export default function PublicJobPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const source = searchParams.get("src") || "direct";
  const navigate = useNavigate();
  const { user } = useAuth() || {};

  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(false);
  const [applyError, setApplyError] = useState(null);

  useGoogleJobsSchema(job);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await publicJob(id);
        setJob(data);
      } catch (e) {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const handleApply = async () => {
    if (!user) {
      navigate(`/login?next=/jobs/${id}?src=${source}`);
      return;
    }
    setApplying(true);
    setApplyError(null);
    try {
      const { data } = await publicJobApply(id, source);
      setApplied(true);
      if (data?.already_applied) setApplied(true);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && detail.toLowerCase().includes("already")) {
        setApplied(true);
      } else {
        setApplyError(detail || "Could not submit application. Please try again.");
      }
    } finally {
      setApplying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#C2410C]" size={32} />
      </div>
    );
  }

  if (notFound || !job) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex flex-col items-center justify-center gap-4 px-4">
        <AlertCircle size={40} className="text-stone-400" />
        <h1 className="font-display text-3xl text-stone-800">Job not found</h1>
        <p className="text-stone-500">This role may have been filled or the link may be incorrect.</p>
        <a href="https://wishire.ai" className="btn-primary mt-2">Browse open roles</a>
      </div>
    );
  }

  const salaryText = job.comp_min && job.comp_max
    ? `₹${(job.comp_min / 100000).toFixed(1)}L – ₹${(job.comp_max / 100000).toFixed(1)}L/yr`
    : job.comp_min
    ? `From ₹${(job.comp_min / 100000).toFixed(1)}L/yr`
    : null;

  const empTypeLabel = (job.employment_type || "full_time").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Navbar */}
      <nav className="border-b border-stone-200 bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <a href="https://wishire.ai" className="font-display text-xl tracking-tight text-stone-900">
            Wishire
          </a>
          <a
            href="https://wishire.ai"
            className="text-sm text-stone-600 hover:text-stone-900 inline-flex items-center gap-1"
          >
            Browse all jobs <ExternalLink size={12} />
          </a>
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left: Job details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Header card */}
            <div className="rounded-3xl bg-white border border-stone-200 p-8">
              <div className="flex items-start gap-5">
                <div className="w-14 h-14 rounded-2xl bg-stone-900 flex items-center justify-center text-white font-display text-xl shrink-0">
                  {(job.company_name || "W")[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-stone-500 text-sm font-medium mb-1">{job.company_name}</p>
                  <h1 className="font-display text-3xl md:text-4xl leading-tight text-stone-900">{job.title}</h1>
                  <div className="flex flex-wrap gap-3 mt-4">
                    {job.location && (
                      <span className="inline-flex items-center gap-1.5 text-sm text-stone-600">
                        <MapPin size={14} className="text-stone-400" />
                        {job.location}
                        {job.remote && " · Remote OK"}
                      </span>
                    )}
                    {!job.location && job.remote && (
                      <span className="inline-flex items-center gap-1.5 text-sm text-stone-600">
                        <MapPin size={14} className="text-stone-400" /> Remote
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1.5 text-sm text-stone-600">
                      <Briefcase size={14} className="text-stone-400" />
                      {empTypeLabel}
                    </span>
                    {salaryText && (
                      <span className="inline-flex items-center gap-1.5 text-sm text-stone-600">
                        <DollarSign size={14} className="text-stone-400" />
                        {salaryText}
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1.5 text-sm text-stone-500">
                      <Calendar size={14} className="text-stone-400" />
                      {job.created_at ? new Date(job.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "Recently posted"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="rounded-3xl bg-white border border-stone-200 p-8">
              <h2 className="font-display text-xl mb-4 text-stone-900">About the role</h2>
              <pre className="whitespace-pre-wrap font-sans text-stone-700 leading-relaxed text-sm md:text-base">{job.description}</pre>
            </div>

            {/* Requirements */}
            {job.requirements?.length > 0 && (
              <div className="rounded-3xl bg-white border border-stone-200 p-8">
                <h2 className="font-display text-xl mb-4 text-stone-900">Must-haves</h2>
                <ul className="space-y-2">
                  {job.requirements.map((r, i) => (
                    <li key={i} className="flex items-start gap-2 text-stone-700 text-sm md:text-base">
                      <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#C2410C] shrink-0" />
                      {r}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Skills */}
            {job.skills?.length > 0 && (
              <div className="rounded-3xl bg-white border border-stone-200 p-8">
                <h2 className="font-display text-xl mb-4 text-stone-900">Skills</h2>
                <div className="flex flex-wrap gap-2">
                  {job.skills.map((s, i) => (
                    <span
                      key={i}
                      className="px-3 py-1.5 rounded-full bg-stone-100 border border-stone-200 text-sm text-stone-700 font-medium"
                    >
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right: Apply CTA */}
          <div className="lg:col-span-1">
            <div className="sticky top-20 space-y-4">
              <div className="rounded-3xl bg-white border border-stone-200 p-7 shadow-sm">
                <p className="font-display text-lg text-stone-900 mb-1">Ready to apply?</p>
                <p className="text-stone-500 text-sm mb-6">
                  Apply via Wishire — get scored by AI and stand out from the crowd.
                </p>

                {applied ? (
                  <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-4 flex items-center gap-3">
                    <CheckCircle size={20} className="text-emerald-600 shrink-0" />
                    <div>
                      <p className="font-medium text-emerald-800 text-sm">Application submitted!</p>
                      <p className="text-emerald-700 text-xs mt-0.5">We'll notify you of any updates.</p>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={handleApply}
                    disabled={applying}
                    data-testid="public-apply-btn"
                    className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#C2410C] text-white hover:bg-orange-700 disabled:opacity-60 transition-colors font-medium text-base"
                  >
                    {applying ? (
                      <><Loader2 size={16} className="animate-spin" /> Submitting…</>
                    ) : (
                      <>Apply via Wishire <ArrowRight size={16} /></>
                    )}
                  </button>
                )}

                {applyError && (
                  <p className="mt-3 text-red-700 text-xs">{applyError}</p>
                )}

                {!user && !applied && (
                  <p className="mt-3 text-center text-stone-400 text-xs">
                    You'll be asked to sign in first.
                  </p>
                )}
              </div>

              {/* Company info */}
              <div className="rounded-3xl bg-white border border-stone-200 p-6">
                <p className="overline mb-3">About the company</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-stone-900 flex items-center justify-center text-white font-display shrink-0">
                    {(job.company_name || "W")[0]}
                  </div>
                  <div>
                    <p className="font-medium text-stone-800">{job.company_name}</p>
                    <p className="text-stone-500 text-xs">Hiring on Wishire</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-stone-200 mt-16 py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-stone-400 text-sm">
            Powered by{" "}
            <a href="https://wishire.ai" className="text-stone-600 hover:text-stone-900 font-medium">
              Wishire
            </a>{" "}
            — India's AI hiring platform
          </p>
          <div className="flex gap-5 text-stone-400 text-sm">
            <a href="https://wishire.ai" className="hover:text-stone-700">Browse jobs</a>
            <a href="https://wishire.ai/login" className="hover:text-stone-700">Sign in</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
